"use client";

import { EffectCard } from "@saas/shared/components/EffectCard";
import {
	EFFECTS_CATALOG,
	type EffectKind,
} from "@saas/shared/config/effects-catalog";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { cn } from "@ui/lib";
import { useTranslations } from "next-intl";
import { useState } from "react";

const FILTERS = ["all", "video", "image"] as const;

/**
 * All / Video / Image + 4:3 整齐网格（SuperMaker 的弹窗和 Templates tab 也是整齐的，格子小、主要扫标题）。
 * 换特效弹窗用（右栏 Templates 10/23 改成同视频页的 WorkbenchTemplates）；当前特效描边。14 个先不做搜索和细分类
 */
export function EffectGrid({
	currentHref,
	columnsClassName,
	onPick,
}: {
	currentHref: string;
	columnsClassName: string;
	onPick?: () => void;
}) {
	const tTabs = useTranslations("assets.tabs");
	// 默认停在当前特效的类型（视频页打开就是 Video），想看别的再点 All / Image
	const [filter, setFilter] = useState<"all" | EffectKind>(
		() =>
			EFFECTS_CATALOG.find((item) => item.href === currentHref)?.kind ??
			"all",
	);
	const items = EFFECTS_CATALOG.filter(
		(item) => filter === "all" || item.kind === filter,
	);

	return (
		<div className="flex flex-col gap-4">
			<div
				role="tablist"
				className="flex w-max gap-0.5 rounded-xl bg-zinc-100 p-1"
			>
				{FILTERS.map((item) => (
					<button
						key={item}
						type="button"
						role="tab"
						aria-selected={filter === item}
						onClick={() => setFilter(item)}
						className={cn(
							"rounded-lg px-3.5 py-1.5 font-medium text-sm transition-colors",
							filter === item
								? "bg-white text-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
								: "text-zinc-500 hover:text-zinc-800",
						)}
					>
						{tTabs(item)}
					</button>
				))}
			</div>
			<div className={cn("grid gap-3", columnsClassName)}>
				{items.map((item) => (
					<EffectCard
						key={item.href}
						item={item}
						uniform
						current={item.href === currentHref}
						sizes="(max-width: 640px) 50vw, 240px"
						onClick={onPick}
					/>
				))}
			</div>
		</div>
	);
}

/** 左栏特效卡上的「Switch」：手机上几乎铺满屏，桌面上居中大弹窗 */
export function EffectPickerDialog({
	open,
	onOpenChange,
	currentHref,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	currentHref: string;
}) {
	const t = useTranslations("effectWorkbench");

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[90dvh] max-w-4xl flex-col gap-4 overflow-hidden p-4 sm:p-6">
				<DialogHeader>
					<DialogTitle>{t("pickerTitle")}</DialogTitle>
				</DialogHeader>
				<div className="min-h-0 overflow-y-auto">
					<EffectGrid
						currentHref={currentHref}
						columnsClassName="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
						onPick={() => onOpenChange(false)}
					/>
				</div>
			</DialogContent>
		</Dialog>
	);
}
